// API Service Layer for Analytics Dashboard
import axios from 'axios';

// API Base URLs
const API_BASE_URL = 'https://cgb-backend.carl-g-bisaillon.workers.dev/api';
const FACEBOOK_API_URL = 'https://graph.facebook.com/v18.0';
const INSTAGRAM_API_URL = 'https://graph.instagram.com/v18.0';
const GA_API_URL = 'https://analyticsdata.googleapis.com/v1beta';

// Create axios instances for different APIs
const facebookAPI = axios.create({
  baseURL: FACEBOOK_API_URL,
  timeout: 10000,
});

const instagramAPI = axios.create({
  baseURL: INSTAGRAM_API_URL,
  timeout: 10000,
});

const googleAnalyticsAPI = axios.create({
  baseURL: GA_API_URL,
  timeout: 10000,
});

const cloudflareAPI = axios.create({
  baseURL: 'https://api.cloudflare.com/client/v4',
  timeout: 10000,
});

// Note: Authentication tokens are handled by the backend API

// Error handling utility
const handleAPIError = (error, platform) => {
  console.error(`${platform} API Error:`, error);
  if (error.response) {
    throw new Error(`${platform} API Error: ${error.response.data.error?.message || error.response.statusText}`);
  } else if (error.request) {
    throw new Error(`${platform} API Error: Network error - please check your internet connection`);
  } else {
    throw new Error(`${platform} API Error: ${error.message}`);
  }
};

// Facebook API Services
export const facebookAPIService = {
  // Get page insights
  getPageInsights: async (pageId, metrics = 'followers_count,reach,impressions,engagement') => {
    try {
      const response = await facebookAPI.get(`/${pageId}/insights`, {
        params: {
          metric: metrics,
          period: 'day',
          since: Math.floor(Date.now() / 1000) - (7 * 24 * 60 * 60), // Last 7 days
          until: Math.floor(Date.now() / 1000),
        }
      });
      return response.data;
    } catch (error) {
      handleAPIError(error, 'Facebook');
    }
  },

  // Get page basic info
  getPageInfo: async (pageId) => {
    try {
      const response = await facebookAPI.get(`/${pageId}`, {
        params: {
          fields: 'name,followers_count,fan_count,website,phone,emails'
        }
      });
      return response.data;
    } catch (error) {
      handleAPIError(error, 'Facebook');
    }
  },

  // Get posts insights
  getPostsInsights: async (pageId, limit = 25) => {
    try {
      const response = await facebookAPI.get(`/${pageId}/posts`, {
        params: {
          fields: 'id,message,created_time,insights.metric(post_impressions,post_engaged_users,post_reactions_by_type_total)',
          limit: limit
        }
      });
      return response.data;
    } catch (error) {
      handleAPIError(error, 'Facebook');
    }
  }
};

// Instagram API Services
export const instagramAPIService = {
  // Get user media
  getUserMedia: async (userId, limit = 25) => {
    try {
      const response = await instagramAPI.get(`/${userId}/media`, {
        params: {
          fields: 'id,caption,media_type,media_url,thumbnail_url,permalink,timestamp,insights.metric(impressions,reach,likes,comments,shares,saved)',
          limit: limit
        }
      });
      return response.data;
    } catch (error) {
      handleAPIError(error, 'Instagram');
    }
  },

  // Get user profile
  getUserProfile: async (userId) => {
    try {
      const response = await instagramAPI.get(`/${userId}`, {
        params: {
          fields: 'id,username,account_type,media_count,followers_count,follows_count'
        }
      });
      return response.data;
    } catch (error) {
      handleAPIError(error, 'Instagram');
    }
  },

  // Get media insights
  getMediaInsights: async (mediaId) => {
    try {
      const response = await instagramAPI.get(`/${mediaId}/insights`, {
        params: {
          metric: 'impressions,reach,likes,comments,shares,saved'
        }
      });
      return response.data;
    } catch (error) {
      handleAPIError(error, 'Instagram');
    }
  }
};

// Google Analytics API Services
export const googleAnalyticsAPIService = {
  // Get basic metrics
  getBasicMetrics: async (propertyId, startDate, endDate) => {
    try {
      // Note: This requires server-side implementation due to OAuth2 requirements
      // For now, we'll use a proxy endpoint
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/google-analytics/metrics`, {
        propertyId,
        startDate,
        endDate,
        metrics: [
          'sessions',
          'screenPageViews',
          'bounceRate',
          'averageSessionDuration',
          'newUsers',
          'returningUsers'
        ]
      });
      return response.data;
    } catch (error) {
      handleAPIError(error, 'Google Analytics');
    }
  },

  // Get real-time data
  getRealTimeData: async (propertyId) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/google-analytics/realtime`, {
        propertyId,
        metrics: ['activeUsers', 'screenPageViews']
      });
      return response.data;
    } catch (error) {
      handleAPIError(error, 'Google Analytics');
    }
  }
};

// Cloudflare API Services
export const cloudflareAPIService = {
  // Get analytics data
  getAnalytics: async (zoneId, startDate, endDate) => {
    try {
      const response = await cloudflareAPI.get(`/zones/${zoneId}/analytics/dashboard`, {
        params: {
          since: startDate,
          until: endDate
        }
      });
      return response.data;
    } catch (error) {
      handleAPIError(error, 'Cloudflare');
    }
  },

  // Get zone details
  getZoneDetails: async (zoneId) => {
    try {
      const response = await cloudflareAPI.get(`/zones/${zoneId}`);
      return response.data;
    } catch (error) {
      handleAPIError(error, 'Cloudflare');
    }
  },

  // Get security events
  getSecurityEvents: async (accountId, startDate, endDate) => {
    try {
      const response = await cloudflareAPI.get(`/accounts/${accountId}/security/events`, {
        params: {
          since: startDate,
          until: endDate
        }
      });
      return response.data;
    } catch (error) {
      handleAPIError(error, 'Cloudflare');
    }
  }
};

// Combined API service
export const analyticsAPI = {
  // Get all platform data from backend
  getAllData: async (timeRange = '7d') => {
    try {
      console.log('Fetching analytics data from backend...');
      const response = await fetch('https://cgb-backend.carl-g-bisaillon.workers.dev/api/analytics/all');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Analytics data received:', result);
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to fetch analytics data');
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      throw error; // Don't return mock data
    }
  }
};

// Helper functions to process API responses
const getFacebookData = async () => {
  const pageId = process.env.REACT_APP_FACEBOOK_PAGE_ID;
  if (!pageId) throw new Error('Facebook Page ID not configured');

  const [pageInfo, insights, posts] = await Promise.all([
    facebookAPIService.getPageInfo(pageId),
    facebookAPIService.getPageInsights(pageId),
    facebookAPIService.getPostsInsights(pageId)
  ]);

  // Process insights data
  const processedInsights = processFacebookInsights(insights);
  const processedPosts = processFacebookPosts(posts);

  return {
    followers: pageInfo.followers_count || 0,
    reach: processedInsights.reach || 0,
    engagement: processedInsights.engagement || 0,
    pageLikes: pageInfo.fan_count || 0,
    postReach: processedPosts.totalReach || 0,
    postEngagement: processedPosts.totalEngagement || 0,
    shares: processedPosts.totalShares || 0,
    trend: calculateTrend(processedInsights.reach, processedInsights.previousReach)
  };
};

const getInstagramData = async () => {
  const userId = process.env.REACT_APP_INSTAGRAM_USER_ID;
  if (!userId) throw new Error('Instagram User ID not configured');

  const [profile, media] = await Promise.all([
    instagramAPIService.getUserProfile(userId),
    instagramAPIService.getUserMedia(userId)
  ]);

  const processedMedia = processInstagramMedia(media);

  return {
    followers: profile.followers_count || 0,
    reach: processedMedia.totalReach || 0,
    engagement: processedMedia.engagementRate || 0,
    impressions: processedMedia.totalImpressions || 0,
    likes: processedMedia.totalLikes || 0,
    comments: processedMedia.totalComments || 0,
    trend: calculateTrend(processedMedia.totalReach, processedMedia.previousReach)
  };
};

const getGoogleAnalyticsData = async (startDate, endDate) => {
  const propertyId = process.env.REACT_APP_GA_PROPERTY_ID;
  if (!propertyId) throw new Error('Google Analytics Property ID not configured');

  const metrics = await googleAnalyticsAPIService.getBasicMetrics(propertyId, startDate, endDate);
  
  return {
    sessions: metrics.sessions || 0,
    pageViews: metrics.screenPageViews || 0,
    bounceRate: metrics.bounceRate || 0,
    avgSessionDuration: metrics.averageSessionDuration || 0,
    newUsers: metrics.newUsers || 0,
    returningUsers: metrics.returningUsers || 0,
    trend: calculateTrend(metrics.sessions, metrics.previousSessions)
  };
};

const getCloudflareData = async (startDate, endDate) => {
  const zoneId = process.env.REACT_APP_CLOUDFLARE_ZONE_ID;
  const accountId = process.env.REACT_APP_CLOUDFLARE_ACCOUNT_ID;
  
  if (!zoneId) throw new Error('Cloudflare Zone ID not configured');

  const [analytics, securityEvents] = await Promise.all([
    cloudflareAPIService.getAnalytics(zoneId, startDate, endDate),
    accountId ? cloudflareAPIService.getSecurityEvents(accountId, startDate, endDate) : null
  ]);

  return {
    requests: analytics.totals?.requests?.all || 0,
    bandwidth: analytics.totals?.bandwidth?.all || 0,
    threats: securityEvents?.result?.length || 0,
    uptime: calculateUptime(analytics),
    trend: calculateTrend(analytics.totals?.requests?.all, analytics.previous?.requests?.all)
  };
};

// Data processing utilities
const processFacebookInsights = (insights) => {
  const data = insights.data || [];
  const reachData = data.find(item => item.name === 'reach');
  const impressionsData = data.find(item => item.name === 'impressions');
  const engagementData = data.find(item => item.name === 'engagement');

  return {
    reach: reachData?.values?.[0]?.value || 0,
    impressions: impressionsData?.values?.[0]?.value || 0,
    engagement: engagementData?.values?.[0]?.value || 0,
    previousReach: reachData?.values?.[1]?.value || 0
  };
};

const processFacebookPosts = (posts) => {
  const postsData = posts.data || [];
  let totalReach = 0;
  let totalEngagement = 0;
  let totalShares = 0;

  postsData.forEach(post => {
    const insights = post.insights?.data || [];
    const reachData = insights.find(item => item.name === 'post_impressions');
    const engagementData = insights.find(item => item.name === 'post_engaged_users');
    const sharesData = insights.find(item => item.name === 'post_reactions_by_type_total');

    totalReach += reachData?.values?.[0]?.value || 0;
    totalEngagement += engagementData?.values?.[0]?.value || 0;
    totalShares += sharesData?.values?.[0]?.value || 0;
  });

  return { totalReach, totalEngagement, totalShares };
};

const processInstagramMedia = (media) => {
  const mediaData = media.data || [];
  let totalReach = 0;
  let totalImpressions = 0;
  let totalLikes = 0;
  let totalComments = 0;
  let totalEngagement = 0;

  mediaData.forEach(item => {
    const insights = item.insights?.data || [];
    const reachData = insights.find(metric => metric.name === 'reach');
    const impressionsData = insights.find(metric => metric.name === 'impressions');
    const likesData = insights.find(metric => metric.name === 'likes');
    const commentsData = insights.find(metric => metric.name === 'comments');

    totalReach += reachData?.values?.[0]?.value || 0;
    totalImpressions += impressionsData?.values?.[0]?.value || 0;
    totalLikes += likesData?.values?.[0]?.value || 0;
    totalComments += commentsData?.values?.[0]?.value || 0;
  });

  totalEngagement = totalLikes + totalComments;
  const engagementRate = totalImpressions > 0 ? (totalEngagement / totalImpressions) * 100 : 0;

  return {
    totalReach,
    totalImpressions,
    totalLikes,
    totalComments,
    totalEngagement,
    engagementRate,
    previousReach: totalReach * 0.9 // Mock previous data
  };
};

const calculateTrend = (current, previous) => {
  if (!previous || previous === 0) return '+0%';
  const change = ((current - previous) / previous) * 100;
  return change >= 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
};

const calculateUptime = (analytics) => {
  // This would need to be calculated based on actual uptime data
  // For now, return a mock value
  return 99.9;
};

export default analyticsAPI;
